import {
  TrendingUp,
  Package,
  DollarSign,
  Users,
  Settings,
  RefreshCw,
} from 'lucide-react'

import { useState, useEffect } from 'react'
import { toast } from 'sonner'

import { Button } from '../components/ui/button'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../components/ui/carousel'


/*----------- Metricas, Componentes y Modales -------------*/
import { SucursalCard } from '../components/multibranch/SucursalCard'
import { KPICards } from '../components/multibranch/KPICards'
import { SalesPerformance } from '../components/multibranch/SalesPerformance'
import { SalesComparisonChart } from '../components/multibranch/SalesComparisonChart'
import { ProfitComparisonChart } from '../components/multibranch/ProfitComparisonChart'
import { TopProductsbyBranch } from '../components/multibranch/TopProductsbyBranch'
import { GoalsConfigurationModal } from '../components/multibranch/modals/GoalsConfigurationModal'
import { multibranchService } from '../services/multibranch.service'
import { useSessionStore } from '../store/auth/auth.store'

type Metrics = {
  branchMetrics: BranchMetrics[]
  branchesCount: number
  consolidatedMetrics: ConsolidatedMetrics
  profitComparison: ComparisonItem[]
  salesComparison: ComparisonItem[]
  salesGoals: SalesGoal[]
  topProductsByBranch: TopProductsByBranch[]
}

type BranchMetrics = {
  address: string
  branch: string
  customers: number
  growth: number
  products: number
  profit: number
  sales: number
}

type ComparisonItem = {
  month: string
  [branch: string]: string | number
}

type SalesGoal = {
  sucursal_id?: number
  branch: string
  dailyGoal: number
  dailyActual: number
  monthlyGoal: number
  monthlyActual: number
  annualGoal: number
  annualActual: number
}

type TopProductsByBranch = {
  branch: string
  product: string
  units: number
}

type ConsolidatedMetrics = {
  totalSales: number
  totalProfit: number
  totalCustomers: number
  totalProductsSold: number
}

const initialSalesGoals = [
  {
    branch: 'Sucursal Centro',
    dailyGoal: 6000,
    dailyActual: 6500,
    monthlyGoal: 150000,
    monthlyActual: 156000,
    annualGoal: 1800000,
    annualActual: 1650000,
  },
  {
    branch: 'Sucursal Norte',
    dailyGoal: 5500,
    dailyActual: 5200,
    monthlyGoal: 140000,
    monthlyActual: 138000,
    annualGoal: 1680000,
    annualActual: 1580000,
  },
  {
    branch: 'Sucursal Sur',
    dailyGoal: 5800,
    dailyActual: 6100,
    monthlyGoal: 145000,
    monthlyActual: 142000,
    annualGoal: 1740000,
    annualActual: 1620000,
  },
]
const consolidatedMetrics = [
  {
    title: 'Ventas Totales',
    value: '$436,000',
    icon: DollarSign,
    iconClassName: 'text-green-500',
    iconContainerClassName: 'bg-green-500/10',
  },
  {
    title: 'Utilidad Total',
    value: '$125,900',
    icon: TrendingUp,
    iconClassName: 'text-primary',
    iconContainerClassName: 'bg-primary/10',
  },
  {
    title: 'Clientes Totales',
    value: '1,195',
    icon: Users,
    iconClassName: 'text-purple-500',
    iconContainerClassName: 'bg-purple-500/10',
  },
  {
    title: 'Productos Vendidos',
    value: '3,550',
    icon: Package,
    iconClassName: 'text-orange-500',
    iconContainerClassName: 'bg-orange-500/10',
  },
]
export function MultiBranch() {
  const canEditGoals = useSessionStore((state) => state.canAction('dashboard.dashboard.edit'))
  const [salesGoals, setSalesGoals] = useState<SalesGoal[]>([])
  const [showGoalsModal, setShowGoalsModal] = useState(false)
  const [metrics, setMetrics] = useState<Metrics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingGoals, setEditingGoals] = useState<SalesGoal[]>([])
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date())
  const [isSavingGoals, setIsSavingGoals] = useState(false)

  const goalsComparison = salesGoals.map((goal) => ({
    branch: goal.branch.replace('Sucursal ', ''),
    Meta: goal.monthlyGoal,
    Real: goal.monthlyActual,
  }))

  /*----------- FUNCIONES FETCH -------------*/
  const fetchMetrics = async () => {
    try {
      setLoading(true)
      setError(null)

      const metricsData = await multibranchService.getMetrics()

      setMetrics(metricsData)
      setSalesGoals(metricsData.salesGoals)
      setEditingGoals(JSON.parse(JSON.stringify(metricsData.salesGoals)))
      setLastUpdated(new Date())
    } catch (error) {
      console.error('Error al obtener las métricas:', error)

      setError('No fue posible cargar las métricas.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMetrics()
  }, [])

  const handleSaveGoals = async () => {
    if (!canEditGoals) return

    try {
      setIsSavingGoals(true)

      await multibranchService.updateGoals(editingGoals)

      setSalesGoals(editingGoals)
      toast.success('Metas de venta actualizadas exitosamente')
      setShowGoalsModal(false)
    } catch (error) {
      toast.error('Ocurrió un error al guardar las metas')
    } finally {
      setIsSavingGoals(false)
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />

          <p className="text-sm text-slate-600">
            Cargando métricas...
          </p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <p className="font-medium text-red-600">
            {error}
          </p>

          <Button
            className="mt-4"
            onClick={fetchMetrics}
          >
            Reintentar
          </Button>
        </div>
      </div>
    )
  }

  if (!metrics) return null;

  const mappedBranches = metrics.branchMetrics.map((b, index) => ({
    id: index + 1,
    name: b.branch,
    address: b.address,
    manager: '',
  }))

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-slate-900">
              Gestión Multi-sucursal
            </h1>
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75"></span>
              <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500"></span>
            </span>
          </div>
          <p className="mt-1 text-sm text-slate-600">
            Vista consolidada de {metrics?.branchesCount || 'todas las'} sucursales • Actualizado: {lastUpdated.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={fetchMetrics} variant="outline" className="bg-white">
            <RefreshCw className="mr-2 h-4 w-4" />
            Actualizar
          </Button>
          {canEditGoals && (
            <Button
              onClick={() => {
                setEditingGoals(JSON.parse(JSON.stringify(salesGoals)))
                setShowGoalsModal(true)
              }}
              variant="default"
            >
              <Settings className="mr-2 h-4 w-4" />
              Configurar Metas
            </Button>
          )}
        </div>
      </div>
      {/* Branch Cards */}
      <div className="relative">
        <Carousel
          opts={{
            align: "start",
            loop: false,
          }}
          className="w-full"
        >
          <CarouselContent className="-ml-2 md:-ml-4">
            {metrics?.branchMetrics.map((branch, index) => {
              return (
                <CarouselItem key={index} className="pl-2 md:pl-4 md:basis-1/2 lg:basis-1/3">
                  <SucursalCard
                    branch={branch}
                    index={index}
                    branchMetrics={metrics?.branchMetrics}
                    salesGoals={salesGoals}
                  />
                </CarouselItem>
              )
            })}
          </CarouselContent>
          <div className="hidden md:block">
            <CarouselPrevious className="-left-4" />
            <CarouselNext className="-right-4" />
          </div>
        </Carousel>
        <p className="mt-4 text-center text-xs text-slate-500 md:hidden">
          Desliza para ver más sucursales
        </p>
      </div>

      {/* KPIs */}
      <KPICards consolidatedMetrics={metrics.consolidatedMetrics} />
      {/* Sales performance */}
      <SalesPerformance goalsComparison={goalsComparison} />

      {/* Sales Comparison Chart */}
      <SalesComparisonChart salesComparison={metrics.salesComparison} />

      {/* Profit Comparison Chart */}
      <ProfitComparisonChart profitComparison={metrics.profitComparison} />

      {/* Top Products by Branch */}
      <TopProductsbyBranch branches={mappedBranches} topProductsByBranch={metrics.topProductsByBranch} />

      {/* Goals Configuration Modal */}
      <GoalsConfigurationModal showGoalsModal={showGoalsModal} setShowGoalsModal={setShowGoalsModal} editingGoals={editingGoals} setEditingGoals={setEditingGoals} handleSaveGoals={handleSaveGoals} isSavingGoals={isSavingGoals} />
    </div>
  )
}
